import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Money } from 'components/Money';
import { Text } from 'components/Text';
import { StoreCartFeesQuery } from 'graphql/Store/queries/StoreCartFeesQuery';
import { isRichTextEmpty } from 'helpers/richText';
import { classNames, useEffect, useMemo, useQuery, useState } from 'libs/react';
import { React } from 'libs/react';
import { buildOptionsPayload, calculateSelectionTotal, getIncludedOptions, getOptionTitle, getProductPrice, getProductTitle, getProposedOptions, getSelectedOptionQuantity } from '../StoreProposedOptions';
import { StoreFeeInfos } from './StoreFeeInfos';
import './StorePriceSummary.scss';
/**
 * Wait before pricing the fees, so a burst of quantity clicks makes one call.
 * @const FEES_DELAY
 * @since 1.0.0
 */
const FEES_DELAY = 300;
/**
 * @component StorePriceSummary
 * @since 1.0.0
 */
export function StorePriceSummary(props) {
    var _a, _b;
    const { lang, saleItems, selectedItems, extrasOnly, nextLabel, processing, onNext } = props;
    const [expanded, setExpanded] = useState(false);
    // The fees are priced by the server on the same rules as the cart. With
    // extras only, the summary shows what is added, not the whole cart.
    const feesInput = useMemo(() => {
        const optionsPayload = buildOptionsPayload(selectedItems, saleItems);
        return {
            language: lang,
            items: selectedItems.map((item) => {
                var _a, _b;
                return ({
                    item: item.id,
                    quantity: item.quantity,
                    options: ((_b = (_a = optionsPayload.find((entry) => entry.itemId === item.id)) === null || _a === void 0 ? void 0 : _a.options) !== null && _b !== void 0 ? _b : []).map((option) => ({ item: option.itemId, quantity: option.quantity }))
                });
            })
        };
    }, [lang, selectedItems, saleItems]);
    const feesKey = JSON.stringify(feesInput);
    const [delayedFeesInput, setDelayedFeesInput] = useState(feesInput);
    useEffect(() => {
        const timeout = setTimeout(() => setDelayedFeesInput(feesInput), FEES_DELAY);
        return () => clearTimeout(timeout);
    }, [feesKey]);
    const [feesData, , feesError, feesResult] = useQuery(StoreCartFeesQuery, {
        variables: { input: delayedFeesInput },
        skip: extrasOnly || delayedFeesInput.items.length === 0,
        fetchPolicy: 'cache-first'
    });
    // Keep the last fees while the next ones load, so the total does not jump.
    const fees = feesError
        ? null
        : ((_b = (_a = (feesData !== null && feesData !== void 0 ? feesData : feesResult.previousData)) === null || _a === void 0 ? void 0 : _a.storeCartFees) !== null && _b !== void 0 ? _b : null);
    if (selectedItems.length === 0) {
        return null;
    }
    const otherFees = extrasOnly ? [] : (fees !== null && fees !== void 0 ? fees : []);
    const feesTotal = otherFees.reduce((acc, fee) => acc + fee.price, 0);
    const total = calculateSelectionTotal(selectedItems, saleItems, extrasOnly) + feesTotal;
    const subtitle = fees && !extrasOnly ? t('before taxes') : t('before taxes and fees');
    const itemCount = selectedItems.reduce((acc, item) => acc + item.quantity, 0);
    const className = classNames('store-price-summary', props.className, {
        'store-price-summary--expanded': expanded
    });
    /**
     * @function onToggle
     * @since 1.0.0
     * @hidden
     */
    function onToggle() {
        setExpanded((current) => !current);
    }
    return (React.createElement("div", { className: className },
        React.createElement("div", { className: "store-price-summary__bar" },
            React.createElement("button", { type: "button", className: "store-price-summary__toggle", "aria-expanded": expanded, onClick: onToggle },
                React.createElement("div", { className: "store-price-summary__toggle-left" },
                    React.createElement(Icon, { value: "shopping-cart", className: "store-price-summary__cart-icon" }),
                    React.createElement("div", { className: "store-price-summary__toggle-text" },
                        React.createElement(Text, { value: t('Estimated total'), className: "store-price-summary__toggle-label" }),
                        React.createElement(Text, { value: `(${subtitle}) - ${itemCount} ${itemCount === 1 ? t('item') : t('items')}`, className: "store-price-summary__toggle-subtitle-label" }))),
                React.createElement("div", { className: "store-price-summary__toggle-right" },
                    React.createElement(Money, { value: total, className: "store-price-summary__toggle-amount" }),
                    React.createElement(Icon, { value: "chevron-down", className: "store-price-summary__toggle-icon" }))),
            onNext && (React.createElement(Button, { style: "primary", size: "small", label: nextLabel !== null && nextLabel !== void 0 ? nextLabel : t('Continue'), disabled: processing, processing: processing, onPress: onNext, className: "store-price-summary__next" }))),
        expanded && (React.createElement("div", { className: "store-price-summary__details" },
            selectedItems.map((selectedItem) => {
                const saleItem = saleItems.find((entry) => entry.id === selectedItem.id);
                if (!saleItem) {
                    return null;
                }
                const productTotal = extrasOnly
                    ? 0
                    : getProductPrice(saleItem) * selectedItem.quantity;
                const includedOptions = extrasOnly
                    ? []
                    : getIncludedOptions(saleItem);
                const selectedOptions = getProposedOptions(saleItem).filter((option) => { var _a, _b; return selectedItem.selectedOptionIds.includes((_b = (_a = option.item) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : ''); });
                return (React.createElement("div", { key: selectedItem.id, className: "store-price-summary__item" },
                    productTotal > 0 && (React.createElement("div", { className: "store-price-summary__line" },
                        React.createElement(Text, { value: getProductTitle(saleItem, lang), className: "store-price-summary__label" }),
                        React.createElement("span", { className: "store-price-summary__quantity" },
                            selectedItem.quantity,
                            "\u00D7"),
                        React.createElement(Money, { value: productTotal, className: "store-price-summary__amount" }))),
                    includedOptions.map((option) => (React.createElement("div", { key: option.id, className: "store-price-summary__line store-price-summary__line--option" },
                        React.createElement(Text, { value: getOptionTitle(option, lang), className: "store-price-summary__label" }),
                        option.price > 0 ? (React.createElement(Money, { value: option.price * option.quantity * selectedItem.quantity, className: "store-price-summary__amount" })) : (React.createElement(Text, { value: t('Included'), className: "store-price-summary__amount" }))))),
                    selectedOptions.map((option) => {
                        const quantity = getSelectedOptionQuantity(selectedItem, option);
                        return (React.createElement("div", { key: option.id, className: "store-price-summary__line store-price-summary__line--option" },
                            React.createElement(Text, { value: `+ ${getOptionTitle(option, lang)}`, className: "store-price-summary__label" }),
                            quantity > 1 && (React.createElement("span", { className: "store-price-summary__quantity" },
                                quantity,
                                "\u00D7")),
                            React.createElement(Money, { value: option.price * quantity, className: "store-price-summary__amount" })));
                    })));
            }),
            otherFees.length > 0 && (React.createElement("div", { className: "store-price-summary__item" },
                React.createElement(Text, { value: t('Other fees'), className: "store-price-summary__fees-title" }),
                otherFees.map((fee, index) => {
                    var _a, _b;
                    return (React.createElement("div", { key: index, className: "store-price-summary__line" },
                        React.createElement("span", { className: "store-price-summary__label" },
                            fee.description || ((_a = fee.fee) === null || _a === void 0 ? void 0 : _a.title) || t('Fee'),
                            ((_b = fee.fee) === null || _b === void 0 ? void 0 : _b.infos) && !isRichTextEmpty(fee.fee.infos) && (React.createElement(StoreFeeInfos, { infos: fee.fee.infos, label: fee.description || fee.fee.title }))),
                        React.createElement(Money, { value: fee.price, className: "store-price-summary__amount" })));
                })))))));
}
